import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  handleErrorToast,
  handleSuccessToast,
  handleWarningToast,
} from 'src/components/molecules';
import { useChangePasswordMutation } from 'src/graphql';
import { PASSWORDS } from '../../../api';
import axios from '../../../axios';
import '../../../css/login/Login.css';
import { ChangePasswordNewCommon, FormValues } from './ChangePasswordNewCommon';

interface Props {
  buttonText?: string;
  onPasswordChangeSuccess?: () => void;
  updateWithToken?: boolean;
  token?: string;
  'data-testid'?: string;
  $margin?: string;
  isLocalised?: boolean;
}

export const ChangePasswordNew = ({
  updateWithToken = false,
  isLocalised = false,
  ...props
}: Props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);

  const updatePassword = useChangePasswordMutation({
    onSuccess: () => {
      handleSuccess();
    },
    onError: (error: unknown) => {
      handleErrors((error as Error).message ?? 'Unable to update password');
    },
    onSettled: () => setIsLoading(false),
  });

  const updatePasswordWithCurrentPassword = async ({
    currentPassword,
    newPassword,
    confirmNewPassword,
  }: FormValues) => {
    setIsLoading(true);
    return await updatePassword.mutate({
      input: {
        current: currentPassword,
        password: newPassword,
        confirmation: confirmNewPassword,
      },
    });
  };

  const updatePasswordWithToken = async ({
    newPassword,
    confirmNewPassword,
  }: FormValues) => {
    if (!props.token) {
      handleErrorToast({
        toastMessage: isLocalised
          ? t('reporting.ReportingChangePassword.invalidToken')
          : 'Invalid token',
      });
      return;
    }
    return await axios.post(PASSWORDS, {
      password: newPassword,
      confirmation: confirmNewPassword,
      token: props.token,
    });
  };

  const handleSuccess = () => {
    if (props.onPasswordChangeSuccess) {
      props.onPasswordChangeSuccess();
    } else {
      handleSuccessToast({
        toastMessage: isLocalised
          ? t('reporting.ReportingChangePassword.passwordUpdated')
          : 'Password updated',
        toastDescription: isLocalised
          ? t('reporting.ReportingChangePassword.passwordUpdatedDescription')
          : 'Your password has been successfully updated. Thanks for taking this step to keep your data secure!',
      });
    }
  };

  const handleErrors = (err: string) => {
    if (err === 'Password has been used before') {
      handleWarningToast({
        toastMessage: isLocalised
          ? t('reporting.ReportingChangePassword.passwordUsedBefore')
          : 'Password reuse not allowed',
        toastDescription: isLocalised
          ? t('reporting.ReportingChangePassword.passwordUsedBeforeDescription')
          : "For your safety, you can't reuse any of your last six passwords. Please choose a new, unique password to keep your account secure and try again.",
      });
    } else if (
      err === 'The current password is wrong. Please check it and try again.'
    ) {
      handleErrorToast({
        toastMessage: isLocalised
          ? t('reporting.ReportingChangePassword.incorrectCurrentPassword')
          : err,
      });
    } else {
      const fallbackErrorText = isLocalised
        ? t('reporting.ReportingChangePassword.errorToast')
        : "Can't create password, invalid token.";
      handleErrorToast({
        toastMessage: err ?? fallbackErrorText,
      });
    }
  };

  const createPassword = async (data: FormValues) => {
    try {
      setIsLoading(true);
      const result = updateWithToken
        ? await updatePasswordWithToken(data)
        : await updatePasswordWithCurrentPassword(data);
      if (result?.data.status === 201 || result?.data.status === 200) {
        handleSuccess();
      }
    } catch (error) {
      const err = (
        error as {
          response: { data: { errors: { message: string }[] } };
        }
      )?.response?.data?.errors[0]?.message;
      handleErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ChangePasswordNewCommon
      createPassword={createPassword}
      isLoading={isLoading}
      buttonText={props.buttonText}
      updateWithToken={updateWithToken}
      data-testid={props['data-testid']}
      isLocalised={isLocalised}
    />
  );
};
